import axios from "axios";
import HostService from "../../../services/host.service";
import AuthService from "../../../services/auth.service";

/**
 * Servicio para el endpoint /usuarios
 * API documentada en ../../../../api/docs/api/usuarios.md
 *
 * El módulo sigue el patrón simplificado del backend (sin DTO/validator
 * propios): las respuestas devuelven el modelo tal cual, sin envolver en
 * { data, message } como sí hacen reponedores o peticiones. GET /usuarios
 * responde un array plano; POST/PUT/DELETE/restaurar responden el usuario
 * directamente en res.data.
 */

/**
 * Mapea un usuario del backend (snake_case + RolesModel anidado) a camelCase.
 *
 * DECISION: esto NO es un modelo. A diferencia de normalizeRango
 * (features/rangos/api/rangos.api.js), que espeja el DTO campo a campo y
 * actúa de facto como una capa de transformación reutilizable, mapUsuario
 * solo toca las dos claves realmente conflictivas del backend (fecha_baja /
 * baja_usuario_id en snake_case, y RolesModel anidado); id, username y email
 * pasan sin tocar mediante el resto (...rest). Es privada, no se exporta y
 * vive solo en esta capa API: el mismo nivel de transformación que ya hacen
 * reponedores.api.js o peticiones.api.js al desenvolver response.data.data,
 * solo que aquí además hay que renombrar dos claves.
 *
 * Tolerante a RolesModel ausente o null: un usuario sin rol existe y es
 * motivo de rechazo en el login (ver docs/api/auth.md), no se puede asumir
 * que siempre llega.
 *
 * @param {Object} usuario - Usuario tal cual lo devuelve el backend
 * @returns {Object} Usuario en camelCase: { id, username, email, fechaBaja, bajaUsuarioId, rol }
 */
const mapUsuario = (usuario) => {
    // Defensa coherente con list()/getRoles(), que ya comprueban Array.isArray:
    // un 200 sin cuerpo no está contemplado en el contrato, pero si llegara,
    // es preferible devolver null a que la desestructuración lance un
    // TypeError con un mensaje técnico que acabaría en un toast del usuario.
    if (!usuario) return null;

    const { fecha_baja, baja_usuario_id, RolesModel, ...rest } = usuario;

    return {
        ...rest,
        fechaBaja: fecha_baja ?? null,
        bajaUsuarioId: baja_usuario_id ?? null,
        rol: RolesModel ? { id: RolesModel.id, nombre: RolesModel.name } : null,
    };
};

/**
 * Construye el payload que espera el backend a partir de los datos del
 * formulario: omite el password si viene vacío (para no borrarlo al editar
 * sin querer cambiarlo, mismo comportamiento que Usuario.toObject() en el
 * modelo legacy que este servicio sustituye) y traduce el id de rol
 * seleccionado (rolId, en español como el resto del dominio) al roleId que
 * exige la API.
 * @param {Object} data - Datos del usuario en camelCase
 * @param {string} data.username - Nombre de usuario
 * @param {string} data.email - Email del usuario
 * @param {string} [data.password] - Password en texto plano; se omite del payload si viene vacío
 * @param {number} [data.rolId] - Id del rol seleccionado
 * @returns {Object} Payload en el formato que espera el body de POST/PUT /usuarios
 */
const toPayload = ({ username, email, password, rolId } = {}) => {
    const payload = { username, email };

    if (password && password.trim() !== "") {
        payload.password = password;
    }

    if (rolId !== null && rolId !== undefined && rolId !== "") {
        payload.roleId = rolId;
    }

    return payload;
};

/**
 * Detecta si el 409 de POST /usuarios indica que el username/email pertenece
 * a un usuario dado de baja, y en ese caso adjunta { isBaja: true, userId }
 * al error para que ModalUsuario (TASK-012) pueda ofrecer restaurar en lugar
 * de un toast genérico de conflicto.
 *
 * DECISION / fragilidad conocida: el backend no expone un código de error
 * estructurado para distinguir este caso del 409 "ya existe" (activo); ambos
 * mensajes contienen "(ID n)", así que extraer el número no basta para
 * diferenciarlos. Lo que discrimina es el propio texto "está/esta dado de
 * baja" (se contemplan las dos tildes por si el backend la corrige). Si el
 * mensaje cambia de redacción y deja de matchear, el error se relanza SIN el
 * flag isBaja: el caso cae del lado seguro, tratado como conflicto genérico,
 * nunca ofreciendo restaurar sobre un usuario que podría estar activo.
 *
 * @param {Object} error - Error de Axios capturado en el catch de create()
 * @returns {Object} El mismo error, con isBaja/userId adjuntos cuando el 409 los indica
 */
const enrichCreateConflictError = (error) => {
    const message = error.response?.data?.message;

    if (error.response?.status === 409 && typeof message === "string") {
        const esDeBaja = /est[áa]\s+dado\s+de\s+baja/i.test(message);

        if (esDeBaja) {
            const idMatch = message.match(/\(ID\s+(\d+)\)/i);
            error.isBaja = true;
            error.userId = idMatch ? Number(idMatch[1]) : null;
        }
    }

    return error;
};

/**
 * Servicio para gestión de usuarios y consulta de roles
 */
class UsuariosService {

    /**
     * GET /usuarios - lista usuarios con su rol, ordenados por username
     * @param {Object} [params] - Parámetros opcionales de filtrado
     * @param {boolean} [params.incluirBajas=false] - Si es true, incluye también los usuarios dados de baja; por defecto el backend solo devuelve activos (fecha_baja IS NULL)
     * @returns {Promise<Array>} Array de usuarios en camelCase
     */
    static async list({ incluirBajas = false } = {}) {
        const filtros = incluirBajas ? { incluirBajas } : {};
        const queryString = new URLSearchParams(filtros).toString();
        const url = HostService.getHost() + "usuarios" + (queryString ? `?${queryString}` : "");

        return axios
            .get(url, AuthService.setToken())
            .then((res) => (Array.isArray(res.data) ? res.data : []).filter(Boolean).map(mapUsuario))
            .catch((e) => { console.log(e.message); throw e; });
    }

    /**
     * GET /usuarios/roles - lista los roles disponibles para poblar el select del formulario
     * Sin permiso RBAC propio: el backend lo expone sin restricción porque el front lo necesita para pintar selects
     * @returns {Promise<Array>} Array de roles tal cual los devuelve el backend, p.ej. [{ id, name }]
     */
    static async getRoles() {
        return axios
            .get(HostService.getHost() + "usuarios/roles", AuthService.setToken())
            .then((res) => (Array.isArray(res.data) ? res.data : []))
            .catch((e) => { console.log(e.message); throw e; });
    }

    /**
     * POST /usuarios - crea un usuario
     * @param {Object} data - Datos del usuario
     * @param {string} data.username - Nombre de usuario
     * @param {string} data.email - Email del usuario
     * @param {string} data.password - Password en texto plano
     * @param {number} [data.rolId] - Id del rol seleccionado
     * @returns {Promise<Object>} Usuario creado, en camelCase
     * @throws {Error} Si el 409 indica username/email de un usuario dado de baja, el error
     *   relanzado trae { isBaja: true, userId }; si es "ya existe" (usuario activo), se
     *   relanza sin ese flag (ver enrichCreateConflictError)
     */
    static async create(data) {
        return axios
            .post(HostService.getHost() + "usuarios", toPayload(data), AuthService.setToken())
            .then((res) => mapUsuario(res.data))
            .catch((e) => { console.log(e.message); throw enrichCreateConflictError(e); });
    }

    /**
     * PUT /usuarios/:id - actualiza username, email, password y rol
     * @param {number} id - Id del usuario a actualizar
     * @param {Object} data - Mismos campos que create(); password se omite del payload si viene vacío, para no borrar la contraseña actual al editar sin cambiarla
     * @returns {Promise<Object>} Usuario actualizado, en camelCase
     */
    static async update(id, data) {
        return axios
            .put(HostService.getHost() + "usuarios/" + encodeURIComponent(id), toPayload(data), AuthService.setToken())
            .then((res) => mapUsuario(res.data))
            .catch((e) => { console.log(e.message); throw e; });
    }

    /**
     * DELETE /usuarios/:id - da de baja al usuario
     *
     * IMPORTANTE: ejecuta una BAJA LÓGICA (marca fecha_baja y baja_usuario_id
     * en el backend), NUNCA un borrado físico. El usuario deja de aparecer en
     * el listado por defecto y no puede autenticarse, pero conserva su
     * histórico y su username (ver docs/api/usuarios.md, sección "Por qué la
     * baja es lógica").
     *
     * @param {number} id - Id del usuario a dar de baja
     * @returns {Promise<Object>} Usuario ya marcado como de baja, en camelCase
     * @throws {Error} 400 si el usuario ya estaba de baja (mensaje del backend en error.response.data.message)
     */
    static async delete(id) {
        return axios
            .delete(HostService.getHost() + "usuarios/" + encodeURIComponent(id), AuthService.setToken())
            .then((res) => mapUsuario(res.data))
            .catch((e) => { console.log(e.message); throw e; });
    }

    /**
     * POST /usuarios/:id/restaurar - reactiva un usuario dado de baja (pone fecha_baja a NULL)
     * @param {number} id - Id del usuario a restaurar
     * @returns {Promise<Object>} Usuario reactivado, en camelCase
     * @throws {Error} 400 si el usuario no estaba de baja (mensaje del backend en error.response.data.message)
     */
    static async restaurar(id) {
        return axios
            .post(HostService.getHost() + "usuarios/" + encodeURIComponent(id) + "/restaurar", {}, AuthService.setToken())
            .then((res) => mapUsuario(res.data))
            .catch((e) => { console.log(e.message); throw e; });
    }
}

export default UsuariosService;
